import { type ConnectorInfo } from '@blaze-shared/api-contracts-domain';
import { findColumn } from '@blaze-shared/schema-domain';
import { type BaseSchema, isWorkflowSchema } from '@blaze-shared/schema-domain';
import { MAIN_INPUT_NAME } from '@blaze-shared/workflow-domain';

import { WORKFLOW_SCHEMA_KEY } from '../registry/schema-provider.registry';

export const SCHEMA_EXISTS_WITHOUT_SCHEMA_ID = 'SCHEMA_EXISTS_WITHOUT_SCHEMA_ID';

export function findSchemaIdForTag(
  tag: string,
  schemas: Map<string | symbol, BaseSchema>,
): string | undefined {
  return Array.from(schemas.values()).find((s) =>
    s.columns.find((c) => c.tagDefinition?.items?.some((t) => t.id === tag)),
  )?.id;
}

export function findSchemaIdForInput(
  inputName: string,
  columns: string[],
  schemas: Map<string | symbol, BaseSchema>,
): string | undefined {
  const schema = schemas.get(inputName);
  columns = columns.slice();

  if (!schema) {
    return undefined;
  }

  let currentColumn = findColumn(schema.columns, columns.shift());
  if (!currentColumn && schema.columns.length === 1 && schema.columns[0].name === '') {
    currentColumn = schema.columns[0];
  }

  if (!currentColumn) {
    return undefined;
  }

  for (const column of columns) {
    currentColumn = findColumn(currentColumn?.dataTypeDef?.columns, column);
    if (!currentColumn) {
      return undefined;
    }
  }

  return schema.id || (schema && SCHEMA_EXISTS_WITHOUT_SCHEMA_ID);
}

export function findSchemaIdForColumn(
  columnIdOrName: string,
  schemas: Map<string | symbol, BaseSchema>,
): string | undefined {
  const keys = [WORKFLOW_SCHEMA_KEY, MAIN_INPUT_NAME, 'input2'];
  let schema: BaseSchema | undefined;

  while (keys.length > 0) {
    const key = keys.shift();
    if (!key) {
      continue;
    }
    schema = schemas.get(key);

    if (schema && findColumn(schema?.columns, columnIdOrName)) {
      return isWorkflowSchema(schema) && schema.tableId ? schema.tableId : schema.id;
    }
  }

  return undefined;
}

export function findSchemaForApiParam(
  apiParam: string,
  connectors: ConnectorInfo[],
): { connectorId: string | undefined; schemaName: string } | undefined {
  for (const connector of connectors) {
    const schema = connector.apiActionSchemaInfos?.find((s) => findColumn(s.columns, apiParam));

    if (schema) {
      return {
        schemaName: schema.name,
        connectorId: connector.id,
      };
    }
  }

  return undefined;
}
